import { asset, consumer, Delay, Execute, IAssetHandle, Loop, SequenceChain, Sound, SoundResource } from "@spawn/core";
import {
	FreegameIntroState as BaseFreegameIntroState,
	FreegameActions,
	FreegameIdleState,
	FreegameOverlay,
	FreegameSettings,
} from "@spawn/freegames";
import { GameMessageType } from "@spawn/modern-ui";
import { AmbientSounds, BackgroundTexture, showBackgroundEvent, SlotMachineButtonId } from "@spawn/slot-machine";

@consumer
export class FreegameIntroState extends BaseFreegameIntroState {
	@asset(SoundResource) public readonly freegameIntroSound!: IAssetHandle<SoundResource>;

	private fgIntroSound!: Sound;

	/**
	 * Mostly copied from the base class, minor changes related to updating the slotface after the init call.
	 */
	public override async onEnter(): Promise<void> {
		this.freegameService.isActive = true;
		this.ambientSoundController.stopSound(AmbientSounds.BaseGame);
		this.fgIntroSound = new Sound(this.freegameIntroSound());
		void this.fgIntroSound.play();

		// Clears locked spin cheat when entering free games.
		this.cheatService.patchState({ nextSpinCheat: undefined, lockedSpinCheat: undefined, lockedSymbolMatrix: undefined });
		this.eventDispatcher.dispatch(showBackgroundEvent(BackgroundTexture.SideGame));

		const overlay = this.freegameService.getOverlay(FreegameOverlay.Intro)!;
		this.overlay = this.overlayService.show(overlay);

		this.addButtonSubscriptions();
		this.setButtonStates();

		this.setGameMessage();

		// Override reason:
		if (this.freegameService.state?.currentRound === 0) {
			await this.networkService.performAction(FreegameActions.FreegameInit);
		}

		// Enable spin button after request is done.
		this.buttonStateController.updateState([SlotMachineButtonId.Spin], { isEnabled: true });
		// Skip the intro screen when auto freegame entry is enabled.
		this.checkAutoFreegamesEntry();
	}

	public override checkAutoFreegamesEntry(): void {
		if (
			this.networkService.isUserSettingEnabled(FreegameSettings.AutoFreegamesEntry) &&
			this.autoplayController.isAutoSpinEnabled
		) {
			let autoSkipCountdown = this.autoFreegamesEntryDuration();

			this.autoFreegamesEntryCountdown = new SequenceChain(
				new Loop(
					new SequenceChain(
						new Execute(() => {
							this.gameMessageService.setCustomGameMessage(
								`${this.i18n.t(this.settingsService.getSetting(FreegameSettings.AutoFreegamesEntry)?.label ?? "")}: ${autoSkipCountdown--}`,
								GameMessageType.Primary
							);
						}),
						new Delay(1)
					),
					this.autoFreegamesEntryDuration()
				),
				new Execute(() => {
					this.stateComplete(FreegameIdleState);
				})
			);
			void this.autoFreegamesEntryCountdown.play();
		}
	}

	public override setGameMessage(): void {
		this.gameMessageService.setCustomGameMessage(this.i18n.t("general.pressSpin"), GameMessageType.Primary);
		this.gameMessageService.setCustomGameMessage(undefined, GameMessageType.Secondary);
	}

	public override onLeave(): void {
		void this.fgIntroSound.stop();
		super.onLeave();
	}
}
